<script setup>
// ============================================================================
// BonBoZ: GÁN MẪU TRẢ LỜI CHO TRANG — cùng khuôn với pageLabels/LabelPicker.vue,
// chỉ đổi nguồn dữ liệu (mẫu trả lời thay cho nhãn) và đích lưu (cấu hình theo
// trang, khoá `canned_response_ids`).
//
// MỘT kho mẫu cấp tài khoản, không phải hai kho: mẫu không gán trang nào là
// mẫu chung (10 trang đều thấy), mẫu có gán thì chỉ trang đó thấy. Vì vậy
// KHÔNG thêm cột vào `canned_responses`, không migration Rails.
//
// Lưu là THAY nguyên danh sách của trang đang chọn. Bỏ tick hết = trang quay
// về chỉ thấy mẫu chung. API kiểm quyền admin phía server (agent nhận 403).
//
// Phần tìm / nhóm theo chủ đề / chép từ trang khác nằm ở `BoChonMuc.vue`,
// dùng chung với màn tag theo trang (issue #148).
// ============================================================================
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import PageConfigsAPI, { loiApi } from 'dashboard/api/pageConfigs';
import {
  pageConfigs,
  loadPageConfigs,
  setPageConfig,
  idMauCuaTrang,
  loiNap,
} from 'dashboard/helper/pageConfig';
import { dsChuDe, napChuDe } from 'dashboard/helper/bonboz/chuDeMau';
import { demThayDoi, vanBanThuan } from 'dashboard/helper/bonboz/boChon';
import Button from 'dashboard/components-next/button/Button.vue';
import ChonMuc from 'dashboard/components/ui/SelectMenu.vue';
import BoChonMuc from '../components/BoChonMuc.vue';

const props = defineProps({
  mau: { type: Array, default: () => [] },
});
const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();
const inboxes = useMapGetter('inboxes/getInboxes');

const trang = ref('');
const chon = ref(new Set());
// Danh sách ĐÃ LƯU của trang — mốc để đếm "thêm N, bỏ M" và tô dòng vừa đổi.
const goc = ref(new Set());
const dangLuu = ref(false);
const loi = ref('');
// KHOÁ bộ chọn tới khi lượt nạp `force` dưới đây về. Tick trong lúc chưa có cấu
// hình thì mốc là tập rỗng: Lưu mở ra và gửi đúng mấy mẫu vừa tick — THAY cả
// danh sách, các mẫu cũ của trang bị xoá. Chờ nửa giây rẻ hơn nhiều.
const dangNap = ref(true);

// `force`: bản đồ có thể đã nạp từ màn Hội thoại, ở đây cần bản mới nhất.
loadPageConfigs(true).finally(() => {
  dangNap.value = false;
});
// Vào thẳng màn Mẫu trả lời thì danh sách trang chưa chắc đã nạp.
if (!inboxes.value.length) store.dispatch('inboxes/get');
napChuDe();

const thayDoi = computed(() => demThayDoi(goc.value, chon.value));
const coThayDoi = computed(() => thayDoi.value.them + thayDoi.value.bo > 0);

// Đổi trang thì nạp lại tick từ cấu hình, không giữ tick của trang trước.
watch(trang, () => {
  goc.value = idMauCuaTrang(trang.value);
  chon.value = new Set(goc.value);
});
// Cấu hình nạp lại (lượt `force` ở trên về muộn, hoặc sau khi lưu) chỉ dời MỐC.
// Trước đây nó ghi đè luôn ô tick: lượt nạp về sau khi admin đã tick vài ô là
// mất sạch những gì vừa tick mà không báo gì. Chỉ đồng bộ ô tick khi người
// dùng chưa sửa gì.
watch(pageConfigs, () => {
  const moi = idMauCuaTrang(trang.value);
  if (!coThayDoi.value) chon.value = new Set(moi);
  goc.value = moi;
});

// 265 mẫu mà chỉ có mã tắt (`gEP`, `đ`) thì không ai nhận ra mẫu nào — hiện
// kèm nội dung và nhóm theo chủ đề sẵn có.
const mucMau = computed(() =>
  props.mau.map(m => ({
    key: Number(m.id),
    nhan: m.short_code,
    phu: vanBanThuan(m.content),
    nhom: m.chu_de_id || null,
  }))
);

const luu = async () => {
  dangLuu.value = true;
  loi.value = '';
  try {
    const ids = [...chon.value];
    // Chỉ gửi khoá của mình — server GỘP, cấu hình per-page của tính năng khác
    // trên cùng trang (màu, bộ thẻ) không bị xoá theo — rồi trả về toàn bộ
    // cấu hình sau khi gộp.
    const { data } = await PageConfigsAPI.update(trang.value, {
      canned_response_ids: ids,
    });
    setPageConfig(trang.value, data);
    // Mốc mới = bản vừa lưu, kể cả khi server lọc bớt id không còn tồn tại.
    goc.value = idMauCuaTrang(trang.value);
    chon.value = new Set(goc.value);
    useAlert(
      ids.length
        ? t('BONBOZ.MAU_TRANG.SAVED', { n: ids.length })
        : t('BONBOZ.MAU_TRANG.CLEARED')
    );
  } catch (e) {
    loi.value = loiApi(e);
  } finally {
    dangLuu.value = false;
  }
};
</script>

<template>
  <woot-modal show size="medium" :on-close="() => emit('close')">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('BONBOZ.MAU_TRANG.TITLE')"
        :header-content="$t('BONBOZ.MAU_TRANG.MODAL_HINT')"
      />
      <div class="flex flex-col gap-3 p-8 pt-4">
        <p v-if="loi" class="m-0 text-sm text-n-ruby-11">{{ loi }}</p>

        <div class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ $t('BONBOZ.MAU_TRANG.PICK_PAGE') }}
          <ChonMuc
            v-model="trang"
            :muc="inboxes"
            hien-avatar
            :all-label="$t('BONBOZ.MAU_TRANG.PICK_PAGE_NONE')"
          />
        </div>

        <p v-if="!trang" class="m-0 text-sm text-n-slate-11">
          {{ $t('BONBOZ.MAU_TRANG.PICK_PAGE_FIRST') }}
        </p>
        <p
          v-else-if="dangNap"
          class="m-0 text-sm text-n-slate-11"
          data-testid="dang-nap-cau-hinh"
        >
          {{ $t('BONBOZ.BO_CHON.DANG_NAP') }}
        </p>
        <!-- Nạp hỏng mà chưa từng có cấu hình thì không biết trang đang có gì:
             lưu lúc này là ghi đè mù, nên không cho chọn. -->
        <p v-else-if="!pageConfigs" class="m-0 text-sm text-n-ruby-11">
          {{ $t('BONBOZ.BO_CHON.KHONG_NAP_DUOC', { loi: loiNap }) }}
        </p>
        <BoChonMuc
          v-else
          v-model="chon"
          :muc="mucMau"
          :nhom="dsChuDe"
          :goc="goc"
          :trang-hien-tai="trang"
          :lay-cua-trang="idMauCuaTrang"
        />

        <div class="flex items-center justify-between gap-2">
          <!-- Bỏ hết là trang quay về CHỈ THẤY MẪU CHUNG — tô hổ phách để
               không ai tưởng đó là "thấy tất cả". -->
          <span
            class="text-sm"
            :class="
              trang && !chon.size && goc.size
                ? 'text-n-amber-11'
                : 'text-n-slate-11'
            "
          >
            {{
              chon.size
                ? $t('BONBOZ.MAU_TRANG.SAVED', { n: chon.size })
                : $t('BONBOZ.MAU_TRANG.ONLY_SHARED')
            }}
          </span>
          <div class="flex gap-2">
            <Button
              :label="$t('BONBOZ.MAU_TRANG.CANCEL')"
              size="sm"
              slate
              faded
              @click="emit('close')"
            />
            <Button
              :label="
                dangLuu
                  ? $t('BONBOZ.MAU_TRANG.SAVING')
                  : coThayDoi
                    ? $t('BONBOZ.BO_CHON.LUU_THAY_DOI', thayDoi)
                    : $t('BONBOZ.MAU_TRANG.SAVE')
              "
              size="sm"
              :is-loading="dangLuu"
              :disabled="!trang || !coThayDoi || dangNap || !pageConfigs"
              @click="luu"
            />
          </div>
        </div>
      </div>
    </div>
  </woot-modal>
</template>
